import React, { useState, useEffect } from 'react';
// Trigger build test for main branch
import { createClient } from '@supabase/supabase-js';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Crown, Zap, ShieldAlert, Camera, Sparkles, X, Clock, ScrollText, Check, ZoomIn, History, Plus, Minus, CreditCard, Bitcoin, Loader2, ArrowLeft, Info } from 'lucide-react';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from './cropUtils';
import TermsModal from './TermsModal';
import PrivacyModal from './PrivacyModal';
import PrismaticBurst from './PrismaticBurst';
import TextType from './TextType';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripePayment from './StripePayment';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
console.log('Stripe Key Loaded:', stripePublishableKey ? 'YES' : 'NO (Check your Railway variables)');
const stripePromise = loadStripe(stripePublishableKey);

// Configurar la nueva URL del backend en Cloudflare Workers
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'https://throne-backend.vicenteff1.workers.dev';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://nwzzumqcsibujcpzcgip.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface King {
  id: string;
  name: string;
  message: string;
  price: number;
  photoUrl: string | null;
  timestamp: number;
  reignEnded?: number;
  duration?: number;
}

function App() {
  const [king, setKing] = useState<King | null>(null);
  const [history, setHistory] = useState<King[]>([]);
  const [reignDuration, setReignDuration] = useState<string>('0s');
  
  // Modals state
  const [showForm, setShowForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [coronationSuccess, setCoronationSuccess] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState<'bitcoin' | 'stripe' | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  
  // Form Data
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [error, setError] = useState('');

  // Terms State
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Crop State
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [expandedMessages, setExpandedMessages] = useState<Record<string, boolean>>({});
  const isMaintenance = process.env.REACT_APP_MAINTENANCE_MODE === 'true'; 

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Timer for live reign duration
    const timer = setInterval(() => {
      if (king && king.timestamp) {
        // Convertir ISO string a milisegundos para hacer la resta
        const diff = Date.now() - new Date(king.timestamp).getTime();
        setReignDuration(formatDuration(diff));
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [king]);

  useEffect(() => {
    // Cargar datos iniciales
    axios.get('/api/king').then(res => setKing(res.data));
    axios.get('/api/history').then(res => setHistory(res.data));

    // Listeners de tiempo real con Supabase
    const channel = supabase
      .channel('public:kings')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'kings' }, (payload) => {
        const newKing: King = {
            id: payload.new.id,
            name: payload.new.name,
            message: payload.new.message,
            price: payload.new.price,
            photoUrl: payload.new.photo_url,
            timestamp: payload.new.timestamp,
            reignEnded: payload.new.reign_ended,
            duration: payload.new.duration
        };
        setKing(newKing);
        setLoading(false);
        setPaymentData(null);
        setPaymentMethod(null);
        
        // Actualiza el historial añadiendo el nuevo rey al principio
        setHistory(prev => [newKing, ...prev].slice(0, 50));
      })
      .subscribe((status, err) => {
        console.log('⚡ Supabase Realtime Status:', status);
        if (err) console.error('❌ Supabase Realtime Error:', err);
      });

    return () => { 
      supabase.removeChannel(channel);
    };
  }, []);

  if (isMaintenance) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center selection:bg-amber-500 selection:text-black overflow-hidden relative">
        <PrismaticBurst
          animationType="rotate3d"
          intensity={2.5}
          speed={1.15}
          distort={8.1}
          paused={false}
          offset={{ x: 0, y: 0 }}
          hoverDampness={0.25}
          rayCount={0}
          mixBlendMode="lighten"
          colors={['#ff7b00', '#8a4e00', '#040301']}
        />
        
        <div className="relative z-10 flex flex-col items-center">
          <div className="mb-8 p-4 bg-white/[0.03] border border-white/10 rounded-3xl shadow-2xl backdrop-blur-sm">
            <Crown size={48} className="text-amber-500" strokeWidth={2.5} />
          </div>
          <h1 className="text-3xl md:text-5xl font-serif font-black text-white mb-4 tracking-tighter uppercase drop-shadow-2xl">
            The Expensive Seat
          </h1>
          <div className="h-1 w-16 bg-amber-500 mx-auto mb-8 rounded-full shadow-[0_0_10px_rgba(245,158,11,0.5)]"></div>
          <p className="text-xl text-neutral-300 font-light italic max-w-md mx-auto leading-relaxed mb-12 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            "We are currently performing technical maintenance. We will be back very soon."
          </p>
          <div className="space-y-4 bg-black/40 backdrop-blur-md p-6 rounded-2xl border border-white/5 shadow-xl">
            <p className="text-[10px] text-neutral-400 uppercase font-black tracking-[0.3em]">For any questions or inquiries:</p>
            <a 
              href="mailto:support@expensiveseat.com" 
              className="text-amber-500 hover:text-amber-400 transition-all font-mono text-lg font-bold border-b-2 border-amber-500/20 pb-1 hover:border-amber-500"
            >
              support@expensiveseat.com
            </a>
          </div>
        </div>
      </div>
    );
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result?.toString() || null);
      });
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const showCroppedImage = async () => {
    try {
      if (imageSrc && croppedAreaPixels) {
        const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
        if (croppedImage) {
            // Validar tamaño antes de avanzar (5MB = 5 * 1024 * 1024 bytes)
            if (croppedImage.size > 5 * 1024 * 1024) {
                setError('The cropped image is too large (Max 5MB). Please zoom in more or use a smaller original image.');
                return;
            }
            setPhoto(croppedImage);
            setImageSrc(null); // Close cropper
            setZoom(1);
            setError(''); // Limpiar errores previos
        }
      }
    } catch (e) {
      console.error(e);
      setError('Error cropping image');
    }
  };

  const handlePrepareBid = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const bidAmount = (king?.price || 0) + 1;

    const formData = new FormData();
    formData.append('name', name);
    formData.append('message', message);
    formData.append('email', email);
    formData.append('amount', bidAmount.toString());
    if (photo) {
        formData.append('photo', photo, 'king.jpg'); 
    }

    try {
      const res = await axios.post('/api/prepare-bid', formData);
      setAmount(bidAmount.toString()); // Guardar el monto para mostrarlo en el botón de pago
      setPaymentData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientSecret = async () => {
    if (!paymentData?.paymentHash) return;
    setLoading(true);
    try {
        const res = await axios.post('/api/create-payment-intent', {
            paymentHash: paymentData.paymentHash
        });
        setClientSecret(res.data.clientSecret);
        setPaymentMethod('stripe');
    } catch (err: any) {
        setError(err.response?.data?.error || "Error initializing Stripe.");
    } finally {
        setLoading(false);
    }
  };

  const toggleMessage = (id: string) => {
    setExpandedMessages(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const loadMoreHistory = async () => {
    const currentLength = history.length;
    try {
      const res = await axios.get(`/api/history?offset=${currentLength}&limit=50`);
      if (res.data.length > 0) {
        setHistory(prev => [...prev, ...res.data]);
      }
    } catch (err) {
      console.error("Failed to load more history", err);
    }
  };

  const resetForm = () => {
    setPaymentData(null);
    setPaymentMethod(null);
    setClientSecret(null);
    setName('');
    setMessage('');
    setEmail('');
    setAmount('');
    setPhoto(null);
    setImageSrc(null);
    setZoom(1);
    setTermsAccepted(false);
  };

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes < 60) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours < 24) {
      return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
    }

    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;

    return `${days}d ${remainingHours}h ${remainingMinutes}m`;
  };

  return (
    <div className="min-h-screen text-white selection:bg-amber-500 selection:text-black overflow-x-hidden relative">
      <PrismaticBurst
        animationType="rotate3d"
        intensity={2.5}
        speed={1.15}
        distort={8.1}
        paused={false}
        offset={{ x: 0, y: 0 }}
        hoverDampness={0.25}
        rayCount={0}
        mixBlendMode="lighten"
        colors={['#ff7b00', '#8a4e00', '#040301']}
      />

      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="px-8 py-6 md:px-10 flex justify-between items-center sticky top-0 z-40 backdrop-blur-sm">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="bg-gradient-to-br from-amber-300 to-amber-600 p-2 rounded-lg shadow-lg shadow-amber-500/20">
              <Crown className="text-black w-5 h-5 md:w-6 md:h-6" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col">
              <h1 className="text-base md:text-xl font-serif font-bold tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-500 leading-none">
                THE EXPENSIVE SEAT
              </h1>
              <p className="text-[9px] md:text-xs text-amber-100 tracking-[0.2em] font-semibold uppercase mt-1 ml-0.5 opacity-80">
                Immortality has a price
              </p>
            </div>
          </motion.div>
          
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setShowHistory(true)}
              className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full border border-white/10 hover:bg-white/5 hover:border-amber-500/50 transition-all group"
            >
              <History size={16} className="text-neutral-400 group-hover:text-amber-500" />
              <span className="text-xs md:text-sm font-medium text-neutral-300 group-hover:text-white">Hall of Fame</span>
            </button>

            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowForm(true)}
              className="hidden md:flex px-6 py-2 bg-gradient-to-r from-amber-600 to-amber-500 text-black font-bold text-sm tracking-wide rounded-full shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 transition-all"
            >
              CLAIM THRONE
            </motion.button>
          </div>
        </header>

        <main className="flex-1 flex flex-col items-center justify-start md:justify-center p-6 pb-4 pt-4 md:pt-0">
          <AnimatePresence mode="wait">
            {king && (
              <motion.div
                key={king.id}
                initial={{ opacity: 0, scale: 0.9, filter: 'blur(10px)' }}
                animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, scale: 1.1, filter: 'blur(20px)' }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="w-full max-w-5xl"
              >
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 to-purple-600 rounded-[2.5rem] blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
                  
                  <div className="relative bg-[#0a0a0a] border border-white/10 rounded-[2rem] overflow-hidden shadow-2xl flex flex-col md:grid md:grid-cols-[1.2fr,1fr]">
                    <div className="relative h-[50vh] md:h-auto md:min-h-[600px] overflow-hidden bg-neutral-900 flex-shrink-0">
                      {king.photoUrl ? (
                        <motion.img 
                          initial={{ scale: 1.1 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 10 }}
                          src={king.photoUrl} 
                          alt="King" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-neutral-800 to-neutral-900">
                          <Crown size={100} className="text-neutral-700 opacity-50" />
                        </div>
                      )}
                      
                      <div className="hidden md:block absolute bottom-8 left-8 bg-black/60 backdrop-blur-md border border-amber-500/30 px-6 py-3 rounded-2xl">
                        <p className="text-xs text-amber-500 uppercase font-bold tracking-wider mb-1">Current Valuation</p>
                        <p className="text-4xl font-mono text-white font-bold tracking-tight">
                          ${king.price.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    <div className="hidden md:flex p-8 md:p-12 lg:p-16 flex-col justify-center relative overflow-y-auto max-h-[600px] md:max-h-none">
                      <div className="absolute top-10 right-10 opacity-10 pointer-events-none">
                         <Trophy size={120} />
                      </div>

                      <div className="relative">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="h-[1px] w-12 bg-amber-500"></div>
                          <span className="text-amber-500 font-bold uppercase tracking-widest text-sm">Reigning Monarch</span>
                        </div>
                        
                        <h2 className={`${
                          king.name.length > 20 ? 'text-2xl md:text-4xl' : 
                          king.name.length > 10 ? 'text-3xl md:text-5xl' : 
                          'text-4xl md:text-6xl'
                        } font-serif font-black text-white leading-[0.9] mb-8 tracking-tight break-words`}>
                          {king.name}
                        </h2>

                        <div className="relative pl-8 mb-6 md:mb-10">
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500 to-transparent rounded-full"></div>
                          <p className={`${
                            king.message.length > 100 ? 'text-lg md:text-xl' :
                            king.message.length > 50 ? 'text-xl md:text-2xl' :
                            'text-2xl md:text-3xl'
                          } text-neutral-300 font-light italic leading-relaxed break-words`}>
                            "{king.message}"
                          </p>
                        </div>

                        <div className="flex items-center gap-4 pt-8 border-t border-white/5">
                          <div className="flex-1 bg-white/5 rounded-xl p-4 border border-white/5">
                            <p className="text-neutral-500 text-xs uppercase font-bold mb-1">Dethrone Price</p>
                            <p className="text-xl text-amber-200 font-mono">${(king.price + 1).toFixed(2)}</p>
                          </div>
                          {king.id !== 'genesis' && (
                            <div className="flex-1 bg-white/5 rounded-xl p-4 border border-white/5 flex flex-col justify-center">
                              <p className="text-neutral-500 text-xs uppercase font-bold mb-1">Current Reign</p>
                              <div className="flex items-center gap-2 text-amber-200/80">
                                 <Clock size={16} />
                                 <span className="text-xl font-mono font-bold">{reignDuration}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* MOBILE CONTENT ONLY */}
                    <div className="flex md:hidden flex-col bg-[#0a0a0a] px-6 py-5 pb-6">
                       <h2 className="text-3xl font-serif font-black text-white leading-tight mb-2 break-words">{king.name}</h2>
                       <p className="text-base text-neutral-400 font-light italic leading-relaxed mb-5">"{king.message}"</p>
                       
                       <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
                           <div className="flex-shrink-0 bg-neutral-900 border border-white/5 rounded-lg p-3 min-w-[110px]">
                              <p className="text-[10px] text-neutral-500 uppercase font-bold mb-1">Valuation</p>
                              <p className="text-lg text-amber-500 font-mono font-bold">${king.price.toFixed(2)}</p>
                           </div>
                           {king.id !== 'genesis' && (
                             <div className="flex-shrink-0 bg-neutral-900 border border-white/5 rounded-lg p-3 min-w-[110px]">
                                <p className="text-[10px] text-neutral-500 uppercase font-bold mb-1">Reign Time</p>
                                <p className="text-lg text-white font-mono font-bold">{reignDuration}</p>
                             </div>
                           )}
                       </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        <footer className="w-full py-6 pb-28 md:py-12 md:pb-12 text-center text-[11px] md:text-[13px] text-white/60 font-medium tracking-wide relative">
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/80 to-transparent -z-10 pointer-events-none"></div>
            <p className="mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">© 2026 The Expensive Seat. Sydney, Australia.</p>
            <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap px-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                <button 
                  onClick={() => setShowTermsModal(true)} 
                  className="hover:text-amber-500 transition-colors cursor-pointer text-white/70"
                >
                  [ Terms & Conditions ]
                </button>
                <span className="text-white/20">•</span>
                <button 
                  onClick={() => setShowPrivacyModal(true)}
                  className="hover:text-amber-500 transition-colors cursor-pointer text-white/70"
                >
                  [ Privacy Policy ]
                </button>
                <span className="text-white/20">•</span>
                <a href="mailto:support@expensiveseat.com" className="hover:text-white transition-colors text-white/70">[ Support: support@expensiveseat.com ]</a>
            </div>
        </footer>
      </div>

      {/* MOBILE FLOATING BUTTON */}
      <div className="fixed bottom-6 left-6 right-6 z-40 md:hidden">
          <motion.button
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowForm(true)}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-black font-black py-4 rounded-2xl shadow-xl shadow-amber-500/20 flex items-center justify-center gap-2 border border-amber-400/20 backdrop-blur-md"
          >
             <Crown size={24} strokeWidth={2.5} />
             CLAIM THRONE (${((king?.price || 0) + 1).toFixed(2)})
          </motion.button>
      </div>


      <AnimatePresence>
        {showHistory && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
            onClick={() => setShowHistory(false)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl bg-[#0f0f0f] border border-white/10 rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[85vh]"
            >
              <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
                <h3 className="text-xl font-serif font-bold text-white flex items-center gap-2">
                  <ScrollText className="text-amber-500 w-5 h-5" />
                  Hall of Fame
                </h3>
                <button onClick={() => setShowHistory(false)} className="p-2 hover:bg-white/10 rounded-full text-neutral-400 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 overflow-y-auto custom-scrollbar">
                {history.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-neutral-600">
                    <History size={48} className="mb-4 opacity-20" />
                    <p className="italic">No legends have fallen yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {history.map((hKing, index) => {
                      const isExpanded = expandedMessages[hKing.id];
                      return (
                        <motion.div 
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          key={hKing.id}
                          className="bg-neutral-900/50 border border-white/5 rounded-xl p-4 flex flex-col gap-4 hover:border-amber-500/30 transition-colors relative overflow-hidden group"
                        >
                          <div className="flex items-center gap-4">
                            <div className="text-xs font-mono text-amber-500/50 font-bold w-6">
                                #{history.length - index}
                            </div>
                                                     <div className="w-12 h-12 rounded-full bg-neutral-800 overflow-hidden flex-shrink-0 border border-white/10">
                                                        {hKing.photoUrl ? (
                                                            <img src={hKing.photoUrl} alt={hKing.name} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center"><Crown size={16} className="text-neutral-600"/></div>
                                                        )}
                                                     </div>                            <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-white truncate">{hKing.name}</h4>
                                <div className="flex items-center gap-2 text-neutral-500 text-[10px] uppercase font-bold mt-1">
                                    <Clock size={10} />
                                    <span>{hKing.duration ? formatDuration(hKing.duration) : '-'}</span>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-amber-500 font-mono font-bold">${hKing.price.toFixed(2)}</p>
                            </div>
                          </div>

                          <div className="pl-10 flex items-start justify-between gap-4">
                            <p className={`flex-1 min-w-0 text-sm text-neutral-400 italic leading-relaxed break-words whitespace-pre-wrap ${!isExpanded ? 'line-clamp-1' : ''}`}>
                                "{hKing.message}"
                            </p>
                            {hKing.message.length > 50 && (
                                <button 
                                    onClick={() => toggleMessage(hKing.id)}
                                    className="flex-shrink-0 w-6 h-6 rounded-full border border-amber-500/30 flex items-center justify-center text-amber-500 hover:bg-amber-500 hover:text-black transition-all"
                                >
                                    {isExpanded ? <Minus size={14} /> : <Plus size={14} />}
                                </button>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
                
                {history.length > 0 && history.length % 50 === 0 && (
                  <div className="mt-6 flex justify-center">
                    <button 
                      onClick={loadMoreHistory}
                      className="px-6 py-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 hover:border-amber-500/50 text-neutral-400 hover:text-white transition-all text-sm font-medium"
                    >
                      Load More Legends
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <TermsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
      <PrivacyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />

      {/* DESKTOP MODAL (Only render on Desktop) */}
      <AnimatePresence>
        {showForm && !isMobile && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="hidden md:flex fixed inset-0 z-50 items-center justify-center p-4 bg-black/60 backdrop-blur-xl"
            onClick={() => { setShowForm(false); resetForm(); }}
          >
             <motion.div 
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-xl bg-[#0f0f0f] border border-white/10 rounded-3xl overflow-hidden shadow-2xl relative"
            >
              {/* Form Header */}
              <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
                <h3 className="text-xl font-serif font-bold text-white flex items-center gap-2">
                  <Sparkles className="text-amber-500 w-5 h-5" />
                  {coronationSuccess ? 'Success' : imageSrc ? 'Crop Your Portrait' : 'Claim The Throne'}
                </h3>
                <button 
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <X size={20} className="text-neutral-400" />
                </button>
              </div>

              {/* Form Content (Duplicated for Desktop) */}
              <div className="p-8 max-h-[80vh] overflow-y-auto custom-scrollbar">
                {coronationSuccess ? (
                  <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="flex flex-col items-center justify-center py-12 text-center relative"
                  >
                    <div className="w-24 h-24 bg-amber-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-amber-500/40">
                       <Crown size={48} className="text-black" />
                    </div>
                    
                    <TextType 
                        text="LONG LIVE THE KING!"
                        as="h4"
                        className="text-3xl font-serif font-black text-white mb-2"
                        typingSpeed={70}
                        loop={false}
                        showCursor={true}
                    />
                    
                    <TextType 
                        text="Your reign begins now"
                        className="text-amber-200 font-bold mb-8 uppercase tracking-widest text-sm"
                        typingSpeed={50}
                        initialDelay={1500}
                        loop={false}
                        showCursor={false}
                    />

                    <div className="space-y-2 text-neutral-400 text-sm">
                        <p>Payment confirmed successfully.</p>
                        <p>The throne is yours.</p>
                    </div>
                  </motion.div>
                ) : imageSrc ? (
                    <div className="flex flex-col gap-6">
                        <div className="relative w-full h-80 bg-black rounded-xl overflow-hidden border border-white/10">
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        </div>
                        <div className="flex items-center gap-4">
                            <ZoomIn size={20} className="text-neutral-500" />
                            <input
                                type="range"
                                value={zoom}
                                min={1}
                                max={3}
                                step={0.1}
                                onChange={(e) => setZoom(Number(e.target.value))}
                                className="w-full accent-amber-500 h-1 bg-white/10 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>
                        
                        {error && (
                          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-2 text-red-400 text-xs">
                            <ShieldAlert size={16} />
                            <span>{error}</span>
                          </div>
                        )}

                        <div className="flex gap-3">
                            <button onClick={() => { setImageSrc(null); setPhoto(null); }} className="flex-1 py-3 font-bold text-neutral-400 hover:text-white transition-colors">Cancel</button>
                            <button onClick={showCroppedImage} className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-all"><Check size={20} /> Set Portrait</button>
                        </div>
                    </div>
                ) : !paymentData ? (
                  <form onSubmit={handlePrepareBid} className="space-y-6">
                    <div className="space-y-4">
                      <div className="group">
                        <div className="flex justify-between items-end mb-2">
                          <label className="text-xs font-bold text-neutral-500 uppercase tracking-wider ml-1 block group-focus-within:text-amber-500 transition-colors">Your Title</label>
                          <span className={`text-xs font-mono font-bold ${name.length === 30 ? 'text-red-500' : 'text-neutral-600'}`}>{name.length}/30</span>
                        </div>
                        <input type="text" required maxLength={30} value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded-xl p-4 text-lg focus:border-amber-500 outline-none transition-all focus:bg-black" placeholder="" />
                      </div>
                      <div className="group">
                        <div className="flex justify-between items-end mb-2">
                           <label className="text-xs font-bold text-neutral-500 uppercase tracking-wider ml-1 block group-focus-within:text-amber-500 transition-colors">What is your message to the world</label>
                           <span className={`text-xs font-mono font-bold ${message.length === 140 ? 'text-red-500' : 'text-neutral-600'}`}>{message.length}/140</span>
                        </div>
                        <textarea required maxLength={140} value={message} onChange={(e) => setMessage(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded-xl p-4 text-lg focus:border-amber-500 outline-none transition-all focus:bg-black resize-none h-28" placeholder="" />
                      </div>

                      <div className="group relative">
                        <div className="flex items-center gap-2 mb-2">
                          <label className="text-xs font-bold text-neutral-500 uppercase tracking-wider ml-1 block group-focus-within:text-amber-500 transition-colors">
                            Email <span className="text-[10px] opacity-50 font-normal">(Optional)</span>
                          </label>
                          <div className="relative group/tooltip">
                            <Info size={14} className="text-neutral-600 hover:text-amber-500 cursor-help transition-colors" />
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-neutral-800 text-[10px] text-neutral-200 rounded-lg shadow-xl opacity-0 group-hover/tooltip:opacity-100 pointer-events-none transition-opacity z-50 border border-white/5 text-center">
                              This field is optional. If this project grows, we'll need your email to keep you updated! :)
                              <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-neutral-800"></div>
                            </div>
                          </div>
                        </div>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded-xl p-4 text-lg focus:border-amber-500 outline-none transition-all focus:bg-black" placeholder="" />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="group">
                          <label className="text-xs font-bold text-neutral-500 uppercase tracking-wider ml-1 mb-2 block group-focus-within:text-amber-500 transition-colors">Dethrone Price</label>
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-500 font-bold">$</span>
                            <div className="w-full bg-black/50 border border-amber-500/30 rounded-xl p-4 pl-8 text-lg font-mono text-white">
                                {((king?.price || 0) + 1).toFixed(2)}
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-neutral-500 uppercase tracking-wider ml-1 mb-2 block">Portrait</label>
                          <input type="file" accept="image/*" id="photo-upload-desktop" onChange={handleFileChange} className="hidden" />
                          <label htmlFor="photo-upload-desktop" className={`w-full h-[62px] border rounded-xl flex items-center justify-center gap-2 cursor-pointer transition-all overflow-hidden relative ${photo ? 'bg-black/50 border-white/10 text-white' : 'bg-black/50 border-white/10 border-dashed hover:border-white/30 text-neutral-400'}`}>
                            {photo ? (
                                <span className="text-sm font-bold">Change</span>
                            ) : (
                                <><Camera size={20} /><span className="text-sm font-medium truncate max-w-[100px]">Upload</span></>
                            )}
                          </label>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 pt-2">
                        <div className="relative flex items-center">
                          <input
                            type="checkbox"
                            id="terms-check-desktop"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border border-white/40 bg-black/50 transition-all checked:border-amber-500 checked:bg-amber-500 hover:border-white/60 flex-shrink-0"
                          />
                          <Check size={14} className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-black opacity-0 peer-checked:opacity-100 transition-opacity" strokeWidth={3} />
                        </div>
                        <label htmlFor="terms-check-desktop" className="text-xs text-neutral-400 leading-relaxed cursor-pointer select-none">
                          I agree to the <button type="button" onClick={() => setShowTermsModal(true)} className="text-amber-500 hover:text-amber-400 hover:underline font-bold">Terms of Service</button>. I understand that refunds are NOT provided if I am dethroned, and I am over 18 years old.
                        </label>
                      </div>
                    </div>
                    {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400"><ShieldAlert size={20} /><span className="text-sm font-medium">{error}</span></div>}
                    <button 
                      disabled={loading || !termsAccepted || !photo} 
                      className="w-full bg-amber-500 hover:bg-amber-400 text-black font-black py-4 rounded-xl flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin w-5 h-5" />
                          ANALYZING CONTENT...
                        </>
                      ) : (
                        <>
                          GENERATE INVOICE <Zap size={20} fill="currentColor" />
                        </>
                      )}
                    </button>
                  </form>
                ) : (
                  <div className="text-center">
                    {!paymentMethod ? (
                      <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <h4 className="text-xl font-bold mb-8">Select Payment Method</h4>
                        
                        <button 
                          onClick={fetchClientSecret}
                          disabled={loading}
                          className="w-full bg-white/5 hover:bg-white/10 border border-white/10 p-4 rounded-xl flex items-center justify-between group transition-all"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400 group-hover:text-blue-300">
                              {loading && paymentMethod === null ? <Loader2 className="animate-spin" size={24} /> : <CreditCard size={24} />}
                            </div>
                            <div className="text-left">
                              <p className="font-bold text-white">Credit Card</p>
                              <p className="text-xs text-neutral-400">Powered by Stripe</p>
                            </div>
                          </div>
                          <div className="w-4 h-4 rounded-full border border-white/20 group-hover:border-amber-500 transition-colors" />
                        </button>

                        <button 
                          onClick={() => {
                            setPaymentMethod('bitcoin');
                            // Registramos el interés en segundo plano
                            axios.post('/api/track-btc-interest').catch(console.error);
                          }}
                          className="w-full bg-white/5 hover:bg-white/10 border border-white/10 p-4 rounded-xl flex items-center justify-between group transition-all"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-orange-500/20 rounded-lg text-orange-400 group-hover:text-orange-300">
                              <Bitcoin size={24} />
                            </div>
                            <div className="text-left">
                              <p className="font-bold text-white">Lightning Network</p>
                              <p className="text-xs text-neutral-400">Pay with Bitcoin</p>
                            </div>
                          </div>
                          <div className="w-4 h-4 rounded-full border border-white/20 group-hover:border-amber-500 transition-colors" />
                        </button>

                        <div className="flex pt-4">
                            <button 
                                onClick={() => setPaymentData(null)} 
                                className="flex items-center gap-2 px-1 py-2 text-neutral-500 hover:text-white transition-colors text-xs font-bold uppercase tracking-widest"
                            >
                                <ArrowLeft size={16} />
                                Go Back
                            </button>
                        </div>
                      </div>
                    ) : paymentMethod === 'stripe' && clientSecret ? (
                      <div className="animate-in fade-in zoom-in duration-300 text-left">
                        <h4 className="text-xl font-bold mb-6 text-center">Credit Card Payment</h4>
                        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' } }}>
                          <StripePayment 
                            paymentHash={paymentData.paymentHash} 
                            amount={amount} 
                            onSuccess={() => {
                                setCoronationSuccess(true);
                                // Timeout eliminado: el usuario debe pulsar el botón
                            }} 
                          />
                        </Elements>
                        <div className="flex pt-4">
                            <button 
                                onClick={() => { setPaymentMethod(null); setClientSecret(null); }} 
                                className="flex items-center gap-2 px-1 py-2 text-neutral-500 hover:text-white transition-colors text-xs font-bold uppercase tracking-widest"
                            >
                                <ArrowLeft size={16} />
                                Go Back
                            </button>
                        </div>
                      </div>
                    ) : paymentMethod === 'bitcoin' ? (
                      <div className="animate-in fade-in zoom-in duration-300 py-12 flex flex-col items-center">
                        <div className="p-4 bg-orange-500/10 rounded-full mb-6 text-orange-400 flex-shrink-0">
                           <Bitcoin size={64} />
                        </div>
                        
                        <div className="w-full text-center mb-3 min-h-[1.5em]">
                            <TextType 
                                text="Bitcoin Coming Soon"
                                as="h4"
                                className="text-2xl font-bold"
                                typingSpeed={30}
                                loop={false}
                                showCursor={true}
                            />
                        </div>

                        <div className="px-8 max-w-md mx-auto mb-12 text-center min-h-[3em]">
                            <TextType 
                                text="We're currently building our Lightning Network integration. We've noted your interest and we'll be working on it soon!"
                                className="text-neutral-400 text-sm leading-relaxed"
                                typingSpeed={15}
                                initialDelay={400}
                                loop={false}
                                showCursor={false}
                            />
                        </div>
                        
                        <div className="flex pt-4 w-full">
                            <button 
                                onClick={() => setPaymentMethod(null)} 
                                className="flex items-center gap-2 px-1 py-2 text-neutral-500 hover:text-white transition-colors text-xs font-bold uppercase tracking-widest"
                            >
                                <ArrowLeft size={16} />
                                Go Back
                            </button>
                        </div>
                      </div>
                    ) : (
                      <div className="py-12 animate-in fade-in zoom-in duration-300">
                        <div className="p-4 bg-blue-500/10 rounded-full inline-block mb-4 text-blue-400">
                           <CreditCard size={48} />
                        </div>
                        <h4 className="text-xl font-bold mb-2">Internal Error</h4>
                        <p className="text-neutral-400 text-sm mb-8 px-8">There was an error initializing the payment. Please try again.</p>
                        <button onClick={() => setPaymentMethod(null)} className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-full text-sm font-bold transition-colors">Go Back</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MOBILE DRAWER (Only render on Mobile) */}
      <AnimatePresence>
        {showForm && isMobile && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`flex md:hidden fixed inset-0 z-50 ${paymentData ? 'items-center p-4' : 'items-end p-0'} bg-black/80`}
            onClick={() => { setShowForm(false); resetForm(); }}
          >
             <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full bg-[#0f0f0f] border-white/10 overflow-hidden shadow-2xl relative flex flex-col ${paymentData ? 'rounded-3xl border max-h-[80vh]' : 'rounded-t-[2rem] border-t max-h-[90vh]'}`}
            >
              {/* Form Header */}
              <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/[0.02] flex-shrink-0">
                <h3 className="text-xl font-serif font-bold text-white flex items-center gap-2">
                  <Sparkles className="text-amber-500 w-5 h-5" />
                  {coronationSuccess ? 'Success' : imageSrc ? 'Crop Your Portrait' : 'Claim The Throne'}
                </h3>
                <button 
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <X size={20} className="text-neutral-400" />
                </button>
              </div>

              {/* Form Content (Duplicated for Mobile) */}
              <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
                {coronationSuccess ? (
                  <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="flex flex-col items-center justify-center py-12 text-center relative"
                  >
                    <div className="w-24 h-24 bg-amber-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-amber-500/40">
                       <Crown size={48} className="text-black" />
                    </div>
                    
                    <TextType 
                        text="LONG LIVE THE KING!"
                        as="h4"
                        className="text-3xl font-serif font-black text-white mb-2"
                        typingSpeed={70}
                        loop={false}
                        showCursor={true}
                    />
                    
                    <TextType 
                        text="Your reign begins now"
                        className="text-amber-200 font-bold mb-8 uppercase tracking-widest text-sm"
                        typingSpeed={50}
                        initialDelay={1500}
                        loop={false}
                        showCursor={false}
                    />

                    <div className="space-y-2 text-neutral-400 text-sm">
                        <p>Payment confirmed successfully.</p>
                        <p>The throne is yours.</p>
                    </div>
                  </motion.div>
                ) : imageSrc ? (
                    <div className="flex flex-col gap-6">
                        <div className="relative w-full h-80 bg-black rounded-xl overflow-hidden border border-white/10">
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        </div>
                        <div className="flex items-center gap-4">
                            <ZoomIn size={20} className="text-neutral-500" />
                            <input
                                type="range"
                                value={zoom}
                                min={1}
                                max={3}
                                step={0.1}
                                onChange={(e) => setZoom(Number(e.target.value))}
                                className="w-full accent-amber-500 h-1 bg-white/10 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>
                        
                        {error && (
                          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-2 text-red-400 text-xs">
                            <ShieldAlert size={16} />
                            <span>{error}</span>
                          </div>
                        )}

                        <div className="flex gap-3">
                            <button onClick={() => { setImageSrc(null); setPhoto(null); }} className="flex-1 py-3 font-bold text-neutral-400 hover:text-white transition-colors">Cancel</button>
                            <button onClick={showCroppedImage} className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-all"><Check size={20} /> Set Portrait</button>
                        </div>
                    </div>
                ) : !paymentData ? (
                  <form onSubmit={handlePrepareBid} className="space-y-6">
                    <div className="space-y-4">
                      <div className="group">
                        <div className="flex justify-between items-end mb-2">
                          <label className="text-xs font-bold text-neutral-500 uppercase tracking-wider ml-1 block group-focus-within:text-amber-500 transition-colors">Your Title</label>
                          <span className={`text-xs font-mono font-bold ${name.length === 30 ? 'text-red-500' : 'text-neutral-600'}`}>{name.length}/30</span>
                        </div>
                        <input type="text" required maxLength={30} value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded-xl p-4 text-lg focus:border-amber-500 outline-none transition-all focus:bg-black" placeholder="" />
                      </div>
                      <div className="group">
                        <div className="flex justify-between items-end mb-2">
                           <label className="text-xs font-bold text-neutral-500 uppercase tracking-wider ml-1 block group-focus-within:text-amber-500 transition-colors">What is your message to the world</label>
                           <span className={`text-xs font-mono font-bold ${message.length === 140 ? 'text-red-500' : 'text-neutral-600'}`}>{message.length}/140</span>
                        </div>
                        <textarea required maxLength={140} value={message} onChange={(e) => setMessage(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded-xl p-4 text-lg focus:border-amber-500 outline-none transition-all focus:bg-black resize-none h-28" placeholder="" />
                      </div>

                      <div className="group relative">
                        <div className="flex items-center gap-2 mb-2">
                          <label className="text-xs font-bold text-neutral-500 uppercase tracking-wider ml-1 block group-focus-within:text-amber-500 transition-colors">
                            Email <span className="text-[10px] opacity-50 font-normal">(Optional)</span>
                          </label>
                          <div className="relative group/tooltip">
                            <Info size={14} className="text-neutral-600 hover:text-amber-500 cursor-help transition-colors" />
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-neutral-800 text-[10px] text-neutral-200 rounded-lg shadow-xl opacity-0 group-hover/tooltip:opacity-100 pointer-events-none transition-opacity z-50 border border-white/5 text-center">
                              This field is optional. If this project grows, we'll need your email to keep you updated! :)
                              <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-neutral-800"></div>
                            </div>
                          </div>
                        </div>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded-xl p-4 text-lg focus:border-amber-500 outline-none transition-all focus:bg-black" placeholder="" />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="group">
                          <label className="text-xs font-bold text-neutral-500 uppercase tracking-wider ml-1 mb-2 block group-focus-within:text-amber-500 transition-colors">Dethrone Price</label>
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-500 font-bold">$</span>
                            <div className="w-full bg-black/50 border border-amber-500/30 rounded-xl p-4 pl-8 text-lg font-mono text-white">
                                {((king?.price || 0) + 1).toFixed(2)}
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-neutral-500 uppercase tracking-wider ml-1 mb-2 block">Portrait</label>
                          <input type="file" accept="image/*" id="photo-upload-mobile" onChange={handleFileChange} className="hidden" />
                          <label htmlFor="photo-upload-mobile" className={`w-full h-[62px] border rounded-xl flex items-center justify-center gap-2 cursor-pointer transition-all overflow-hidden relative ${photo ? 'bg-black/50 border-white/10 text-white' : 'bg-black/50 border-white/10 border-dashed hover:border-white/30 text-neutral-400'}`}>
                            {photo ? (
                                <span className="text-sm font-bold">Change</span>
                            ) : (
                                <><Camera size={20} /><span className="text-sm font-medium truncate max-w-[100px]">Upload</span></>
                            )}
                          </label>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 pt-2">
                        <div className="relative flex items-center">
                          <input
                            type="checkbox"
                            id="terms-check-mobile"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border border-white/40 bg-black/50 transition-all checked:border-amber-500 checked:bg-amber-500 hover:border-white/60 flex-shrink-0"
                          />
                          <Check size={14} className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-black opacity-0 peer-checked:opacity-100 transition-opacity" strokeWidth={3} />
                        </div>
                        <label htmlFor="terms-check-mobile" className="text-xs text-neutral-400 leading-relaxed cursor-pointer select-none">
                          I agree to the <button type="button" onClick={() => setShowTermsModal(true)} className="text-amber-500 hover:text-amber-400 hover:underline font-bold">Terms of Service</button>. I understand that refunds are NOT provided if I am dethroned, and I am over 18 years old.
                        </label>
                      </div>
                    </div>
                    {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400"><ShieldAlert size={20} /><span className="text-sm font-medium">{error}</span></div>}
                    <button 
                      disabled={loading || !termsAccepted || !photo} 
                      className="w-full bg-amber-500 hover:bg-amber-400 text-black font-black py-4 rounded-xl flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin w-5 h-5" />
                          ANALYZING CONTENT...
                        </>
                      ) : (
                        <>
                          GENERATE INVOICE <Zap size={20} fill="currentColor" />
                        </>
                      )}
                    </button>
                  </form>
                ) : (
                  <div className="text-center">
                    {!paymentMethod ? (
                      <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <h4 className="text-xl font-bold mb-8">Select Payment Method</h4>
                        
                        <button 
                          onClick={fetchClientSecret}
                          disabled={loading}
                          className="w-full bg-white/5 hover:bg-white/10 border border-white/10 p-4 rounded-xl flex items-center justify-between group transition-all"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400 group-hover:text-blue-300">
                              {loading && paymentMethod === null ? <Loader2 className="animate-spin" size={24} /> : <CreditCard size={24} />}
                            </div>
                            <div className="text-left">
                              <p className="font-bold text-white">Credit Card</p>
                              <p className="text-xs text-neutral-400">Powered by Stripe</p>
                            </div>
                          </div>
                          <div className="w-4 h-4 rounded-full border border-white/20 group-hover:border-amber-500 transition-colors" />
                        </button>

                        <button 
                          onClick={() => {
                            setPaymentMethod('bitcoin');
                            // Registramos el interés en segundo plano
                            axios.post('/api/track-btc-interest').catch(console.error);
                          }}
                          className="w-full bg-white/5 hover:bg-white/10 border border-white/10 p-4 rounded-xl flex items-center justify-between group transition-all"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-orange-500/20 rounded-lg text-orange-400 group-hover:text-orange-300">
                              <Bitcoin size={24} />
                            </div>
                            <div className="text-left">
                              <p className="font-bold text-white">Lightning Network</p>
                              <p className="text-xs text-neutral-400">Pay with Bitcoin</p>
                            </div>
                          </div>
                          <div className="w-4 h-4 rounded-full border border-white/20 group-hover:border-amber-500 transition-colors" />
                        </button>

                        <div className="flex pt-4">
                            <button 
                                onClick={() => setPaymentData(null)} 
                                className="flex items-center gap-2 px-1 py-2 text-neutral-500 hover:text-white transition-colors text-xs font-bold uppercase tracking-widest"
                            >
                                <ArrowLeft size={16} />
                                Go Back
                            </button>
                        </div>
                      </div>
                    ) : paymentMethod === 'stripe' && clientSecret ? (
                      <div className="animate-in fade-in zoom-in duration-300 text-left">
                        <h4 className="text-xl font-bold mb-6 text-center">Credit Card Payment</h4>
                        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' } }}>
                          <StripePayment 
                            paymentHash={paymentData.paymentHash} 
                            amount={amount} 
                            onSuccess={() => {
                                setCoronationSuccess(true);
                                // Timeout eliminado: el usuario debe pulsar el botón
                            }} 
                          />
                        </Elements>
                        <div className="flex pt-4">
                            <button 
                                onClick={() => { setPaymentMethod(null); setClientSecret(null); }} 
                                className="flex items-center gap-2 px-1 py-2 text-neutral-500 hover:text-white transition-colors text-xs font-bold uppercase tracking-widest"
                            >
                                <ArrowLeft size={16} />
                                Go Back
                            </button>
                        </div>
                      </div>
                    ) : paymentMethod === 'bitcoin' ? (
                      <div className="animate-in fade-in zoom-in duration-300 py-12 flex flex-col items-center">
                        <div className="p-4 bg-orange-500/10 rounded-full mb-6 text-orange-400 flex-shrink-0">
                           <Bitcoin size={64} />
                        </div>
                        
                        <div className="w-full text-center mb-3 min-h-[1.5em]">
                            <TextType 
                                text="Bitcoin Coming Soon"
                                as="h4"
                                className="text-2xl font-bold"
                                typingSpeed={30}
                                loop={false}
                                showCursor={true}
                            />
                        </div>

                        <div className="px-8 max-w-md mx-auto mb-12 text-center min-h-[3em]">
                            <TextType 
                                text="We're currently building our Lightning Network integration. We've noted your interest and we'll be working on it soon!"
                                className="text-neutral-400 text-sm leading-relaxed"
                                typingSpeed={15}
                                initialDelay={400}
                                loop={false}
                                showCursor={false}
                            />
                        </div>
                        
                        <div className="flex pt-4 w-full">
                            <button 
                                onClick={() => setPaymentMethod(null)} 
                                className="flex items-center gap-2 px-1 py-2 text-neutral-500 hover:text-white transition-colors text-xs font-bold uppercase tracking-widest"
                            >
                                <ArrowLeft size={16} />
                                Go Back
                            </button>
                        </div>
                      </div>
                    ) : (
                      <div className="py-12 animate-in fade-in zoom-in duration-300">
                        <div className="p-4 bg-blue-500/10 rounded-full inline-block mb-4 text-blue-400">
                           <CreditCard size={48} />
                        </div>
                        <h4 className="text-xl font-bold mb-2">Internal Error</h4>
                        <p className="text-neutral-400 text-sm mb-8 px-8">There was an error initializing the payment. Please try again.</p>
                        <button onClick={() => setPaymentMethod(null)} className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-full text-sm font-bold transition-colors">Go Back</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;